/**
 * Utilities for portal email action buttons (trash / mark-as-resolved).
 *
 * These helpers decide when lifecycle action buttons should be available
 * and format backend error responses into user-friendly toast messages.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * Decision outcomes that the email monitor agent can emit.
 *
 * Mirrors `DecisionOutcome` in `db/models/email_monitor_decision.py`.
 * Kept as a plain string union so this module has no backend imports.
 */
export const ACTION_ITEM_DECISIONS = ['notify', 'unsure'] as const;
export type ActionItemDecision = (typeof ACTION_ITEM_DECISIONS)[number];

/**
 * An email is an "action item" when the email-monitor agent decided it
 * needs human attention (NOTIFY or UNSURE).
 */
export function isActionItemEmail(email: unknown): boolean {
  if (!email || typeof email !== 'object') return false;
  // Support both the API's snake_case (notification_decision) and the
  // camelCase form produced by mapEmailToComponent (notificationDecision).
  const e = email as Record<string, unknown>;
  const decision =
    (e.notificationDecision as string | null | undefined) ??
    (e.notification_decision as string | null | undefined) ??
    null;
  if (!decision) return false;
  return (ACTION_ITEM_DECISIONS as readonly string[]).includes(decision);
}

/**
 * Whether the "Mark as Resolved" / "Undo Resolve" button should be shown
 * for this email.
 *
 * Gates on business logic only — "is this an action item?" — and not on
 * the presence of a populated `decisionNotificationId`.  The backend
 * dismiss-action endpoint persists resolution on
 * `EmailMonitorDecision.is_resolved` for orphan decisions (no linked
 * Notification row), so the button is fully functional even when the
 * notification side-effect never materialised.
 */
export function canResolveActionItem(email: unknown): boolean {
  return isActionItemEmail(email);
}

/**
 * Pull a human-readable message out of an Axios-style error object.
 *
 * Falls back to ``fallback`` when the error shape is unrecognised so
 * callers always have something to display in a toast.
 */
export function extractApiErrorMessage(err: unknown, fallback: string): string {
  if (!err) return fallback;
  const anyErr = err as any;
  const detail =
    anyErr?.response?.data?.detail
    ?? anyErr?.response?.data?.message
    ?? anyErr?.message;
  if (typeof detail === 'string' && detail.trim().length > 0) {
    return detail;
  }
  return fallback;
}
